import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StaffShell } from "@/components/layout/StaffShell";
import { adminNav } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CenterSpinner } from "@/components/common/States";

const ROLES = ["customer", "vendor", "driver", "admin"];

export default function AdminUsers() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: () => api("/admin/users") });

  const changeRole = async (u, role) => {
    try {
      await api("/admin/grant-role", { method: "POST", json: { email: u.email, role } });
      toast.success(`${u.email} → ${role}`);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  const filtered = (data || []).filter(
    (u) => !q || u.email?.toLowerCase().includes(q.toLowerCase()) || u.name?.toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <StaffShell items={adminNav} roleLabel="Admin" title="Users">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" className="mb-4 max-w-sm rounded-full" data-testid="user-search" />
      {isLoading ? (
        <CenterSpinner />
      ) : (
        <Card className="overflow-hidden rounded-2xl">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((u) => (
                <TableRow key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    {u.is_primary_admin ? (
                      <Badge className="rounded-full font-normal">Primary admin</Badge>
                    ) : (
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                        <SelectTrigger className="h-8 w-32 rounded-full" data-testid={`role-select-${u.user_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </StaffShell>
  );
}
